import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { goToCook } from "../../utils/nav";
import {
  X,
  Clock,
  Users,
  ChefHat,
  Star,
  Plus,
  Sparkles,
  Save,
  Share2,
} from "lucide-react";
import { Button } from "../ui/button";
import { ButtonGroup } from "../../../components/ui/button-group";
import { trackEvent } from "../../config/analytics";
import type { PersonalizationPreviewResult } from "../../../types/personalization";
import { Badge } from "../ui/badge";
import { ImageWithFallback } from "../figma/ImageWithFallback";
import { getRecipeFallbackImage } from "../../utils/recipeImage";
import { cn } from "../ui/utils";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import {
  savePersonalizedRecipe,
  ensureExternalRecipe,
} from "wasp/client/operations";
import { ShareRecipeDialog } from "../recipe/ShareRecipeDialog";
import { toast } from "sonner";
import { MealPlanPicker, MealPlanPickerMobile } from "../meal-plan";
import { MEAL_PLAN_FEATURES } from "../../../config/features";
import { useAuth } from "wasp/client/auth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { useIsMobile } from "../../../hooks/use-mobile";

import {
  normalizeIngredientLine,
  formatStructuredIngredient,
} from "../../../shared/ingredientFormat";
// Align with shared Recipe shape but allow flexible ingredient/instruction forms
interface Recipe {
  id: string;
  title: string;
  image?: string;
  imageUrl?: string;
  heroImageUrl?: string;
  description?: string;
  prepTime?: number;
  cookTime?: number;
  totalMinutes?: number;
  readyInMinutes?: number;
  difficulty?: string;
  cuisine?: string;
  tags?: string[];
  calories?: number;
  servings?: number;
  rating?: number;
  reviewCount?: number;
  // Can be string[] (preview) or RecipeIngredient[] (server response)
  ingredients?: any[];
  // Can be string[] (preview) or RecipeStep[] (server response under `steps`)
  instructions?: string[];
  // Optional server shape for steps
  steps?: Array<{ id: string; text: string }>;
}

interface RecipeDetailModalProps {
  recipe: Recipe | null;
  isOpen: boolean;
  onClose: () => void;
  onAddToPlan?: (
    recipeId: string,
    dayIndex?: number,
    mealType?: string,
    preview?: PersonalizationPreviewResult | null,
  ) => Promise<void>;
  onPreviewPersonalize?: (recipeId: string) => void;
  preview?: PersonalizationPreviewResult | null;
  previewLoading?: boolean;
  previewError?: string | null;
  addingToPlan?: boolean;
}

// PersonalizationPreviewResult comes from @src/types/personalization

const getDifficultyColor = (difficulty?: string) => {
  switch (difficulty?.toLowerCase()) {
    case "easy":
      return "bg-sage/10 text-sage border-sage/20";
    case "medium":
      return "bg-coral/10 text-coral border-coral/20";
    case "hard":
      return "bg-red-100 text-red-800 border-red-200";
    default:
      return "bg-stone/10 text-supporting border-stone/20";
  }
};

export function RecipeDetailModal({
  recipe,
  isOpen,
  onClose,
  onAddToPlan,
  onPreviewPersonalize,
  preview,
  previewLoading = false,
  previewError = null,
  addingToPlan = false,
}: RecipeDetailModalProps) {
  if (!isOpen || !recipe) return null;
  const navigate = useNavigate();
  const location = useLocation();

  // Defaults: today relative to week start, dinner
  const today = new Date();
  const start = new Date(today);
  start.setDate(today.getDate() - today.getDay());
  start.setHours(0, 0, 0, 0);
  const defaultDay = Math.max(0, Math.min(6, today.getDay()));
  const [dayIndex, setDayIndex] = React.useState<number>(defaultDay);
  const [mealType, setMealType] = React.useState<string>("dinner");
  const [viewMode, setViewMode] = React.useState<"original" | "personalized">(
    "original",
  );
  const [savedRecipeId, setSavedRecipeId] = React.useState<string | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = React.useState(false);
  const [isSavingPersonalized, setIsSavingPersonalized] = React.useState(false);
  const [showMealPlanPicker, setShowMealPlanPicker] = React.useState(false);

  const { data: user } = useAuth();
  const isMobile = useIsMobile();
  const pointerDownOnBackdrop = React.useRef(false);

  const handleBackdropPointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      pointerDownOnBackdrop.current = event.target === event.currentTarget;
    },
    [],
  );

  const handleBackdropPointerUp = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (pointerDownOnBackdrop.current && event.target === event.currentTarget) {
        onClose();
      }
      pointerDownOnBackdrop.current = false;
    },
    [onClose],
  );

  React.useEffect(() => {
    setViewMode(preview?.entry ? "personalized" : "original");
  }, [recipe?.id, preview?.entry]);

  const handleStartCooking = React.useCallback(async () => {
    try {
      trackEvent("cooking_mode_started", {
        recipeId: recipe.id,
        source: "discover_modal",
      });
    } catch {}

    const isExternal = /^\d+$/.test(recipe.id);
    if (isExternal) {
      try {
        const result: any = await ensureExternalRecipe({
          externalId: recipe.id,
        });
        goToCook(navigate, location, (result as any)?.recipeId || recipe.id);
        onClose();
      } catch (error) {
        console.error("Failed to save recipe for cooking:", error);
        toast.error("Failed to prepare recipe for cooking. Please try again.");
      }
    } else {
      goToCook(navigate, location, recipe.id);
      onClose();
    }
  }, [navigate, onClose, recipe.id, location.pathname, location.search]);

  const handleOpenShareDialog = React.useCallback(() => {
    if (!savedRecipeId) return;
    try {
      trackEvent("recipe_share_opened" as any, {
        recipeId: savedRecipeId,
        source: "discover_modal",
      });
    } catch {}
    setShareDialogOpen(true);
  }, [savedRecipeId]);

  const handleSavePersonalized = async () => {
    if (!preview?.entry) return;

    setIsSavingPersonalized(true);
    try {
      const total =
        totalTime || preview.entry?.prepTime || recipe.prepTime || 0;
      const result: any = await savePersonalizedRecipe({
        baseRecipeId: recipe.id,
        personalizedData: {
          title: preview.entry.title,
          ingredients: preview.entry.ingredients || [],
          instructions: preview.entry.instructions || [],
          servings: preview.entry.servings || 2,
          prepTime: preview.entry.prepTime,
          tags: preview.entry.tags,
          image: preview.entry.image,
          totalMinutes: total,
          difficulty: displayRecipe.difficulty,
          cuisine: displayRecipe.cuisine,
          calories:
            (displayRecipe as any)?.calories ??
            (displayRecipe as any)?.nutritionInfo?.calories,
        },
        visibility: "private",
      });

      if ((result as any)?.success) {
        setSavedRecipeId((result as any).recipeId);
        toast.success("Personalized recipe saved to your collection!");
      }
    } catch (error) {
      console.error("Failed to save personalized recipe:", error);
      toast.error("Failed to save recipe. Please try again.");
    } finally {
      setIsSavingPersonalized(false);
    }
  };

  const dayLabels = Array.from({ length: 7 }).map((_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d.toLocaleDateString(undefined, { weekday: "long" });
  });

  const handleAddToPlan = async () => {
    try {
      trackEvent("recipe_add_to_plan_clicked" as any, {
        recipeId: recipe.id,
        source: "discover_modal",
      });
    } catch {}

    if (MEAL_PLAN_FEATURES.MOBILE_PICKER || MEAL_PLAN_FEATURES.OPTIMISTIC_UPDATES) {
      setShowMealPlanPicker(true);
    } else {
      // Fallback to legacy onAddToPlan if feature flags disabled
      await onAddToPlan?.(recipe.id, dayIndex, mealType, preview ?? null);
    }
  };

  const handlePickerSuccess = () => {
    setShowMealPlanPicker(false);
    toast.success('Recipe added to meal plan!');
  };

  const isDbRecipe = typeof recipe.id === "string" && !/^\d+$/.test(recipe.id);

  const previewAsRecipe = React.useMemo<Recipe | null>(() => {
    if (!preview?.entry) return null;
    const entry = preview.entry;
    return {
      ...recipe,
      id: entry.id,
      title: entry.title || recipe.title,
      image:
        entry.image ||
        recipe.image ||
        (recipe as any).heroImageUrl ||
        (recipe as any).imageUrl,
      imageUrl: entry.image || (recipe as any).imageUrl,
      heroImageUrl: (recipe as any).heroImageUrl,
      prepTime: entry.prepTime ?? recipe.prepTime,
      cookTime: (entry as any).cookTime ?? recipe.cookTime,
      totalMinutes: (entry as any).totalMinutes ?? (recipe as any).totalMinutes,
      difficulty: recipe.difficulty,
      cuisine: recipe.cuisine,
      tags: entry.tags && entry.tags.length ? entry.tags : recipe.tags,
      calories: recipe.calories,
      servings: entry.servings ?? recipe.servings,
      rating: recipe.rating,
      reviewCount: recipe.reviewCount,
      ingredients:
        entry.ingredients && entry.ingredients.length
          ? entry.ingredients
          : recipe.ingredients,
      instructions:
        entry.instructions && entry.instructions.length
          ? entry.instructions
          : recipe.instructions,
      description: recipe.description,
    };
  }, [preview?.entry, recipe]);

  const displayRecipe =
    viewMode === "personalized" && previewAsRecipe ? previewAsRecipe : recipe;

  const totalTime = (() => {
    const tm = (displayRecipe as any).totalMinutes;
    if (typeof tm === "number" && tm > 0) return tm;
    const sum = (displayRecipe.prepTime ?? 0) + (displayRecipe.cookTime ?? 0);
    if (sum > 0) return sum;
    const ready = (displayRecipe as any).readyInMinutes;
    if (typeof ready === "number" && ready > 0) return ready;
    return 0;
  })();
  const previewReady = !!preview?.entry;

  // Normalize ingredients to displayable strings using shared utilities
  const formatIngredient = (ing: any): string => {
    if (typeof ing === "string") return normalizeIngredientLine(ing);
    if (ing && typeof ing === "object") {
      // Try structured format first
      if (ing.amount !== undefined || ing.unit || ing.name) {
        return formatStructuredIngredient({
          amount: ing.amount || ing.quantity,
          unit: ing.unit,
          name:
            ing.name ||
            ing.ingredient?.name ||
            ing.notes ||
            ing.ingredientId ||
            "",
          note: ing.note || ing.notes,
          raw: ing.raw || ing.quantity,
        });
      }
      // Fallback to string conversion
      return normalizeIngredientLine(String(ing));
    }
    return "";
  };

  // Normalize instructions: prefer explicit instructions (string[]),
  // fall back to steps[].text if present.
  const instructionItems: string[] = Array.isArray(displayRecipe.instructions)
    ? displayRecipe.instructions
    : Array.isArray((displayRecipe as any).steps)
      ? ((displayRecipe as any).steps as Array<{ text: string }>).map(
          (s) => s.text,
        )
      : [];

  const startCookingButtonContent = (
    <>
      <ChefHat className="mr-2 h-4 w-4" />
      Start Cooking
    </>
  );

  const addToPlanButtonContent = addingToPlan ? (
    <>
      <div className="border-ink/20 mr-2 h-4 w-4 animate-spin rounded-full border-2 border-t-ink" />
      Adding...
    </>
  ) : (
    <>
      <Plus className="mr-2 h-4 w-4" />
      Add to Plan
    </>
  );

  const renderShareMenu = () => {
    if (!savedRecipeId) return null;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Share recipe"
            className="shrink-0"
          >
            <Share2 className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={4}
          onClick={(event) => event.stopPropagation()}
        >
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              handleOpenShareDialog();
            }}
          >
            Manage sharing…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const shareMenuDesktop = renderShareMenu();
  const shareMenuMobile = renderShareMenu();

  return (
    <div
      role="presentation"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onPointerDown={handleBackdropPointerDown}
      onPointerUp={handleBackdropPointerUp}
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-stone p-4">
          <div>
            <h2 className="flex items-center gap-2 font-header text-xl font-semibold text-dusk">
              Recipe Details
              {viewMode === "personalized" && previewReady && (
                <span className="border-sage/20 bg-sage/10 text-sage inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs">
                  <Sparkles className="h-3 w-3" />
                  Personalized
                </span>
              )}
            </h2>
            {previewReady && (
              <p className="text-supporting mt-1 text-xs">
                Showing{" "}
                {viewMode === "personalized" ? "personalized" : "original"}{" "}
                recipe. Toggle below to switch views.
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content (scrollable) */}
        <div className="flex-1 overflow-y-auto">
          {/* Recipe Image */}
          <div className="relative aspect-video">
            <ImageWithFallback
              src={
                displayRecipe.image ||
                (displayRecipe as any).imageUrl ||
                (displayRecipe as any).heroImageUrl ||
                getRecipeFallbackImage({ w: 600, h: 400 })
              }
              alt={displayRecipe.title}
              className="h-full w-full object-cover"
            />
            {/* Overlay Info */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent">
              <div className="absolute bottom-4 left-4 right-4">
                <h1 className="mb-2 font-header text-2xl font-bold text-white">
                  {displayRecipe.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-white/90">
                  {totalTime > 0 && (
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>{totalTime} min</span>
                    </div>
                  )}
                  {displayRecipe.servings && (
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      <span>{displayRecipe.servings} servings</span>
                    </div>
                  )}
                  {displayRecipe.difficulty && (
                    <div className="flex items-center gap-1">
                      <ChefHat className="h-4 w-4" />
                      <span className="capitalize">
                        {displayRecipe.difficulty}
                      </span>
                    </div>
                  )}
                  {displayRecipe.rating && (
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 fill-warning text-warning" />
                      <span>{displayRecipe.rating}</span>
                      {displayRecipe.reviewCount && (
                        <span className="text-xs">
                          ({displayRecipe.reviewCount})
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Recipe Details */}
          <div className="space-y-6 p-6">
            {/* Tags and Nutrition */}
            <div className="flex flex-wrap gap-3">
              {displayRecipe.difficulty && (
                <Badge
                  variant="outline"
                  className={cn(
                    "font-medium",
                    getDifficultyColor(displayRecipe.difficulty),
                  )}
                >
                  {displayRecipe.difficulty}
                </Badge>
              )}
              {displayRecipe.cuisine && (
                <Badge
                  variant="secondary"
                  className="bg-stone/10 text-supporting"
                >
                  {displayRecipe.cuisine}
                </Badge>
              )}
              {displayRecipe.calories && (
                <Badge
                  variant="secondary"
                  className="bg-stone/10 text-supporting"
                >
                  {displayRecipe.calories} cal
                </Badge>
              )}
              {displayRecipe.tags?.slice(0, 3).map((tag, index) => (
                <Badge
                  key={index}
                  variant="secondary"
                  className="bg-stone/10 text-supporting"
                >
                  {tag}
                </Badge>
              ))}
            </div>

            {/* Description */}
            {displayRecipe.description && (
              <div>
                <h3 className="mb-2 font-semibold text-dusk">Description</h3>
                <p className="text-supporting leading-relaxed">
                  {displayRecipe.description}
                </p>
              </div>
            )}

            {/* Add-to-plan controls - Hidden when using new MealPlanPicker */}
            {!MEAL_PLAN_FEATURES.MOBILE_PICKER && !MEAL_PLAN_FEATURES.OPTIMISTIC_UPDATES && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-supporting mb-1 block text-xs">
                    Day
                  </label>
                  <select
                    className="w-full rounded border border-stone px-2 py-1 text-sm"
                    value={dayIndex}
                    onChange={(e) => setDayIndex(Number(e.target.value))}
                  >
                    {dayLabels.map((lbl, i) => (
                      <option key={i} value={i}>
                        {lbl}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-supporting mb-1 block text-xs">
                    Meal
                  </label>
                  <select
                    className="w-full rounded border border-stone px-2 py-1 text-sm"
                    value={mealType}
                    onChange={(e) => setMealType(e.target.value)}
                  >
                    <option value="breakfast">Breakfast</option>
                    <option value="lunch">Lunch</option>
                    <option value="dinner">Dinner</option>
                  </select>
                </div>
              </div>
            )}

            {isDbRecipe && (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onPreviewPersonalize?.(recipe.id)}
                  disabled={previewLoading || addingToPlan}
                  className="flex items-center gap-2"
                >
                  {previewLoading ? (
                    <>
                      <div className="border-ink/20 h-3.5 w-3.5 animate-spin rounded-full border-2 border-t-ink" />
                      Generating preview...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      {previewReady
                        ? "Refresh personalized preview"
                        : "Preview personalized recipe"}
                    </>
                  )}
                </Button>

                {previewReady && (
                  <div className="bg-stone/10 flex items-center gap-2 rounded-full p-1">
                    <Button
                      type="button"
                      variant={viewMode === "original" ? "default" : "ghost"}
                      size="sm"
                      onClick={() => setViewMode("original")}
                      className={cn(
                        "px-3 py-1 text-xs",
                        viewMode === "original"
                          ? "bg-white text-ink shadow-sm"
                          : "text-supporting",
                      )}
                    >
                      Original
                    </Button>
                    <Button
                      type="button"
                      variant={
                        viewMode === "personalized" ? "default" : "ghost"
                      }
                      size="sm"
                      onClick={() => setViewMode("personalized")}
                      className={cn(
                        "px-3 py-1 text-xs",
                        viewMode === "personalized"
                          ? "bg-white text-ink shadow-sm"
                          : "text-supporting",
                      )}
                    >
                      Personalized
                    </Button>
                  </div>
                )}
              </div>
            )}

            {previewError && (
              <p className="text-sm text-red-600">{previewError}</p>
            )}

            {/* Ingredients */}
            {Array.isArray(displayRecipe.ingredients) &&
              displayRecipe.ingredients.length > 0 && (
                <div>
                  <h3 className="mb-3 font-semibold text-dusk">Ingredients</h3>
                  <ul className="space-y-2">
                    {displayRecipe.ingredients.map((ingredient, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-coral" />
                        <span className="text-supporting">
                          {formatIngredient(ingredient)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            {/* Instructions */}
            {instructionItems.length > 0 && (
              <div>
                <h3 className="mb-3 font-semibold text-dusk">Instructions</h3>
                <ol className="space-y-3">
                  {instructionItems.map((instruction, index) => (
                    <li key={index} className="flex gap-3">
                      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-coral text-sm font-semibold text-ink">
                        {index + 1}
                      </span>
                      <span className="text-supporting leading-relaxed">
                        {instruction}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Personalized Recipe Save Section (inside scroll) */}
            {preview?.entry && viewMode === "personalized" && (
              <div className="border-stone/20 from-sage/5 -mx-6 border-t bg-gradient-to-r to-coral/5 p-4">
                <div className="mx-6">
                  <Alert className="border-sage/30 mb-3 bg-white/80">
                    <Sparkles className="text-sage h-4 w-4" />
                    <AlertTitle>Personalized for You</AlertTitle>
                    <AlertDescription>
                      This recipe has been customized based on your dietary
                      preferences and kitchen profile.
                    </AlertDescription>
                  </Alert>

                  <div className="flex gap-2">
                    <Button
                      onClick={handleSavePersonalized}
                      disabled={isSavingPersonalized || !!savedRecipeId}
                      className="bg-sage desktop-hover:bg-sage/90 flex-1 font-medium text-white"
                    >
                      {isSavingPersonalized ? (
                        <>
                          <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white/20 border-t-white" />
                          Saving...
                        </>
                      ) : savedRecipeId ? (
                        <>
                          <Star className="mr-2 h-4 w-4" />
                          Saved to Collection
                        </>
                      ) : (
                        <>
                          <Save className="mr-2 h-4 w-4" />
                          Save as My Recipe
                        </>
                      )}
                    </Button>

                    {savedRecipeId && (
                      <Button
                        onClick={handleOpenShareDialog}
                        variant="outline"
                        size="icon"
                        className="shrink-0"
                        aria-label="Share recipe"
                      >
                        <Share2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="border-t border-stone bg-gray-50 p-4">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <Button
                variant="outline"
                onClick={onClose}
                className="w-full sm:w-auto"
                aria-label="Close recipe details"
              >
                Close
              </Button>
              <div className="hidden w-full sm:flex sm:w-auto sm:items-center sm:justify-end sm:gap-2">
                {shareMenuDesktop}
                <ButtonGroup>
                  <Button
                    onClick={handleStartCooking}
                    className="bg-orange-500 font-medium text-white hover:bg-orange-600"
                    aria-label="Start cooking this recipe"
                  >
                    {startCookingButtonContent}
                  </Button>
                  <Button
                    onClick={handleAddToPlan}
                    disabled={addingToPlan}
                    className="bg-coral font-medium text-ink desktop-hover:bg-coral/90"
                    aria-label="Add recipe to meal plan"
                  >
                    {addToPlanButtonContent}
                  </Button>
                </ButtonGroup>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:hidden">
              {shareMenuMobile && (
                <div className="flex justify-end">{shareMenuMobile}</div>
              )}
              <Button
                onClick={handleStartCooking}
                className="w-full bg-orange-500 font-medium text-white hover:bg-orange-600"
                aria-label="Start cooking this recipe"
              >
                {startCookingButtonContent}
              </Button>
              <Button
                onClick={handleAddToPlan}
                disabled={addingToPlan}
                className="w-full bg-coral font-medium text-ink desktop-hover:bg-coral/90"
                aria-label="Add recipe to meal plan"
              >
                {addToPlanButtonContent}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Share Recipe Dialog */}
      {savedRecipeId && (
        <ShareRecipeDialog
          recipe={{
            id: savedRecipeId,
            title: preview?.entry?.title || recipe.title,
          }}
          isOpen={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
        />
      )}

      {/* Meal Plan Picker */}
      {showMealPlanPicker && user && (
        MEAL_PLAN_FEATURES.MOBILE_PICKER && isMobile ? (
          <MealPlanPickerMobile
            recipeId={recipe.id}
            recipeTitle={displayRecipe.title}
            userId={user.id}
            onClose={() => setShowMealPlanPicker(false)}
            onSuccess={handlePickerSuccess}
          />
        ) : (
          <MealPlanPicker
            recipeId={recipe.id}
            recipeTitle={displayRecipe.title}
            userId={user.id}
            onClose={() => setShowMealPlanPicker(false)}
            onSuccess={handlePickerSuccess}
          />
        )
      )}
    </div>
  );
}
